import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AssessmentOverviewAdapter } from 'components/Assessments/PatAssessment/sections/AssessmentOverview/adapter'
import { PatientBackgroundAdapter } from 'components/Assessments/PatAssessment/sections/PatientBackground/adapter'
import { ModifierAAdapter } from 'components/Assessments/PatAssessment/sections/ModifierA/adapter'
import { BradenScaleAdapter } from 'components/Assessments/PatAssessment/sections/BradenScale/adapter'
import { ModifierBAdapter } from 'components/Assessments/PatAssessment/sections/ModifierB/adapter'
import { CNASkilledTasksAdapter } from 'components/Assessments/PatAssessment/sections/CNASkilledTasks/adapter'
import { ScoreAndSummaryAdapter } from 'components/Assessments/PatAssessment/sections/ScoreAndSummary/adapter'
import { NextStepsAdapter } from 'components/Assessments/PatAssessment/sections/NextSteps/adapter'
import { PatFormData } from 'components/Assessments/PatAssessment/types/PatFormData'
import { BaseSubsectionAdapter } from './BaseSubsectionAdapter'
import { BaseAssessmentAdapter } from './BaseAssessmentAdapter'
import { AdapterOptions, SectionAdapter } from './types'
import { AssessmentType } from '../../Clinical/Assessments/constants'
import { mutateFlatAnswers } from '../helpers'

/**
 * Array of all PAT section adapter instances
 */
const PAT_SECTION_ADAPTERS: BaseSubsectionAdapter<PatFormData>[] | SectionAdapter<PatFormData>[] = [
  new AssessmentOverviewAdapter(),
  new PatientBackgroundAdapter(),
  new ModifierAAdapter(),
  new BradenScaleAdapter(),
  new ModifierBAdapter(),
  new CNASkilledTasksAdapter(),
  new ScoreAndSummaryAdapter(),
  new NextStepsAdapter()
]

export class PatAssessmentAdapter extends BaseAssessmentAdapter<PatFormData> {
  readonly assessmentType = AssessmentType.PAT

  protected flatAnswersToFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    options?: AdapterOptions
  ): PatFormData {
    const formData: Partial<PatFormData> = {}

    for (const adapter of PAT_SECTION_ADAPTERS) {
      const sectionData = adapter.extractFormData(flatAnswers, options)
      Object.assign(formData, sectionData)
    }

    return formData as PatFormData
  }

  protected formDataToFlatAnswers(
    formData: PatFormData,
    currentFlatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Record<string, QuestionnaireResponseItem> {
    // Use composable section adapters for clean, maintainable transformation
    const allMutations: Array<{ linkId: string; value: unknown }> = []

    // Build mutations from all section adapters dynamically
    for (const adapter of PAT_SECTION_ADAPTERS) {
      if (adapter instanceof BaseSubsectionAdapter) {
        adapter.setFormData(formData)
        allMutations.push(...adapter.buildFlatAnswers())
      } else {
        allMutations.push(...adapter.buildFlatAnswers(formData))
      }
    }

    // Apply all mutations using existing helper
    mutateFlatAnswers(currentFlatAnswers, allMutations)

    // Filter out empty answer objects (objects with only undefined values that serialize to {})
    for (const linkId in currentFlatAnswers) {
      const answerItem = currentFlatAnswers[linkId]
      if (answerItem.answer) {
        // Filter out answer objects that have only undefined values
        const filteredAnswers = answerItem.answer.filter((answer) => {
          // Check if answer object has any non-undefined values
          return Object.values(answer).some((value) => value !== undefined)
        })
        // Set answer to undefined if all answers were filtered out, otherwise use filtered array
        answerItem.answer = filteredAnswers.length > 0 ? filteredAnswers : undefined
      }
    }

    return currentFlatAnswers
  }
}
